"use client"

import { useState, useEffect, useRef } from "react"
import "./ProductDesignProcess.css"

const ProductDesignProcess = () => {
  const [activeTab, setActiveTab] = useState("research") // Default to 'research' tab
  const headerTitleRef = useRef(null)
  const topParagraphRef = useRef(null)
  const mainDescriptionRef = useRef(null)
  const imageRef = useRef(null)
  const tabsRef = useRef(null)

  const tabContent = {
    research: {
      title: "RESEARCH",
      listItems: ["1. BRIEFING", "2. BENCHMARK", "3. USER RESEARCH"],
      paragraphs: [
        "We identify the main aspects of the business model and user needs. First, we gather evidence that will support the decisions taken henceforth, ensuring that no decision is made based on vague assumptions.",<br></br>,
        "This stage consists of 3 steps: a briefing with the project’s vision and goals and business requirements; user research to guarantee product usefulness and effectiveness from the user’s perspective; and a design benchmark to analyze the landscape of similar and complementary products, design patterns and technologies used in the Industry.",<br></br>,
        "The Research stage allows us to leverage the knowledge and skill of existing players and ensure features and design differentiation in the market, and ensure customer fit.",
      ],
    },
    ideation: {
      title: "IDEATION",
      listItems: ["1. BRAINSTORMING", "2. WIREFRAMING", "3. PROTOTYPING"],
      paragraphs: [
        "In the ideation phase, we transform concepts into tangible designs. This involves brainstorming sessions to generate creative solutions, followed by wireframing to outline the basic structure and flow of the product.",<br></br>,
        "Prototypes are then created to simulate the user experience, allowing for early feedback and iterative improvements before development begins.",<br></br>,
        "This ensures that the final product is not only functional but also intuitive and user-friendly.",
      ],
    },
    execution: {
      title: "EXECUTION",
      listItems: ["1. DEVELOPMENT", "2. TESTING", "3. DEPLOYMENT"],
      paragraphs: [
        "The execution phase brings the design to life. Our development team builds the product using agile methodologies, ensuring flexibility and responsiveness to changes.",<br></br>,
        "Rigorous testing is conducted at every stage to identify and fix bugs, ensuring a high-quality and stable product.",<br></br>,
        "Finally, the product is deployed, making it accessible to users and ready for market adoption.",
      ],
    },
    technical_assessment: {
      title: "TECHNICAL ASSESSMENT",
      listItems: ["1. INFRASTRUCTURE REVIEW", "2. SECURITY AUDIT", "3. SCALABILITY ANALYSIS"],
      paragraphs: [
        "A thorough technical assessment ensures the product's robustness and future-readiness. We review the underlying infrastructure to ensure it meets performance and reliability standards.",<br></br>,
        "Security audits are conducted to protect against vulnerabilities and ensure data integrity. A scalability analysis helps plan for future growth and increased user loads.",<br></br>,
        "This comprehensive assessment guarantees that the product is built on a solid technical foundation.",
      ],
    },
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 },
    )

    if (headerTitleRef.current) observer.observe(headerTitleRef.current)
    if (topParagraphRef.current) observer.observe(topParagraphRef.current)
    if (mainDescriptionRef.current) observer.observe(mainDescriptionRef.current)
    if (imageRef.current) observer.observe(imageRef.current)
    if (tabsRef.current) observer.observe(tabsRef.current)

    return () => {
      if (headerTitleRef.current) observer.unobserve(headerTitleRef.current)
      if (topParagraphRef.current) observer.unobserve(topParagraphRef.current)
      if (mainDescriptionRef.current) observer.unobserve(mainDescriptionRef.current)
      if (imageRef.current) observer.unobserve(imageRef.current)
      if (tabsRef.current) observer.unobserve(tabsRef.current)
    }
  }, [])

  return (
    <div className="scoping-container" id="components">
      <div className="container">
      <div className="scoping-wrapper">
        {/* Top Section Grid: Title (left) and New Paragraph (right) */}
        <div className="intro-grid">
          <div className="header-title-wrapper animate-left" ref={headerTitleRef}>
            <h2 className="header-title645">
              OUR
              <br />
              <span className="highlight">PDP PROCESS</span>
            
            </h2>
          </div>
          <div className="top-right-paragraph animate-left" ref={topParagraphRef}>
            <p>
              At Imaginary Cloud, we believe the process of designing a product should be user-centered integrating an
              innovative and practical approach that considers the users’ goals, the business requirements, and a
              top-quality technical side.
            </p>
          </div>

          {/* Main Content Grid: Long Description (left) and Image (right) */}
          <div className="main-description-wrapper animate-left" ref={mainDescriptionRef}>
            <p className="main1-description">
              Behind a web or mobile app’s look & feel, there’s way more than just bright and engaging user interfaces.
              Since UX design is a user-centered discipline, our UI/UX designers focus on the user’s journey and overall
              usability.‍
              <br />
              <br />
              To deliver a world-class digital product, it’s recommended to conduct user research, create user personas,
              plan the information architecture, design flows and wireframes, create prototypes and finalize with
              usability tests. This is why we’ve created the Product Design Process.
              <br />
              <br />  
              By following the PDP, we ensure that no steps are skipped even in the ideation/prototype stage. It lowers
              the risk of finding usability issues or any other problem later on during customer’s adoption or rollout
              phases.
            </p>
          </div>
          <div className="image-section animate-right" ref={imageRef}>
            <img
              src="/process-scoping.webp?height=300&width=400"
              alt="Scoping Process Illustration"
              className="process-image"
            />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-container" ref={tabsRef}>
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === "research" ? "active" : ""}`}
              onClick={() => setActiveTab("research")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
              RESEARCH
            </button>
            <button
              className={`tab-button ${activeTab === "ideation" ? "active" : ""}`}
              onClick={() => setActiveTab("ideation")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                  <line x1="12" y1="7" x2="12" y2="17" />
                  <line x1="7" y1="12" x2="17" y2="12" />
                </svg>
              </div>
              IDEATION
            </button>
            <button
              className={`tab-button ${activeTab === "execution" ? "active" : ""}`}
              onClick={() => setActiveTab("execution")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              EXECUTION
            </button>
            <button
              className={`tab-button ${activeTab === "technical_assessment" ? "active" : ""}`}
              onClick={() => setActiveTab("technical_assessment")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 14v4" />
                  <path d="M12 6v2" />
                  <path d="M12 2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
                  <path d="M12 10a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z" />
                  <path d="M12 18a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z" />
                </svg>
              </div>
              TECHNICAL ASSESSMENT
            </button>
          </div>

          <div className="tab-content">
            {Object.keys(tabContent).map((key) => {
              const content = tabContent[key]
              return (
                <div key={key} className={`tab-panel ${activeTab === key ? "active" : ""}`}>
                  <h3 className="tab-title">{content.title}</h3>
                  {content.listItems && (
                    <ul className="tab-list">
                      {content.listItems.map((item, index) => (
                        <li key={index} className="tab-item">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  {content.paragraphs && (
                    <>
                      {content.paragraphs.map((paragraph, index) => (
                        <p key={index} className="tab-description">
                          {paragraph}
                        </p>
                      ))}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <h3 className="cta-title">NEED A QUOTE FOR YOUR PROJECT?</h3>
          <p className="cta-description">
            Our team of business developers and project managers are ready to analyze your questions. Have a coffee,
            free time to explain us everything and get a quote for your project.
          </p>
          <button className="quote-button">GET A QUOTE</button>
        </div>
      </div>
      </div>
    </div>
  )
}

export default ProductDesignProcess
